import React, { useEffect, useState } from 'react';
import { EChartsOption } from 'echarts';
import BaseChart from './BaseChart';
import { artistAnalyticsService } from '../../services/api/artist-analytics.service';
import type { MonthlyListenersData, MonthlyListenersTable } from '../../services/api/artist-analytics.service';
import { getCategoryAxisLayout } from '../../utils/echartsCategoryAxis';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';

interface DateRange {
  from: Date;
  to: Date;
}

interface MonthlyFollowersChartProps {
  artistId?: string;
  dateRange?: DateRange;
  title?: string;
  className?: string;
  refreshKey?: number;
}

const MonthlyFollowersChart: React.FC<MonthlyFollowersChartProps> = ({
  artistId,
  dateRange,
  title = 'Monthly Followers',
  className = '',
  refreshKey = 0
}) => {
  const [data, setData] = useState<MonthlyListenersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [artistName, setArtistName] = useState<string>('');

  // Generate dynamic summary text based on date range
  const getSummaryText = (): string => {
    if (!dateRange || !dateRange.from || !dateRange.to) {
      return 'LAST 30 DAYS';
    }

    const diffTime = Math.abs(dateRange.to.getTime() - dateRange.from.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // If the difference is around common intervals, use simple text
    if (diffDays <= 1) {
      return 'TODAY';
    } else if (diffDays <= 7) {
      return 'LAST 7 DAYS';
    } else if (diffDays >= 28 && diffDays <= 31) {
      return 'LAST 30 DAYS';
    } else if (diffDays >= 89 && diffDays <= 92) {
      return 'LAST 3 MONTHS';
    } else if (diffDays >= 179 && diffDays <= 183) {
      return 'LAST 6 MONTHS';
    } else if (diffDays >= 364 && diffDays <= 366) {
      return 'LAST YEAR';
    } else {
      // For custom ranges, show the actual dates
      const fromFormatted = dateRange.from.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: dateRange.from.getFullYear() !== dateRange.to.getFullYear() ? 'numeric' : undefined 
      });
      const toFormatted = dateRange.to.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
      return `FROM ${fromFormatted.toUpperCase()} - ${toFormatted.toUpperCase()}`;
    }
  };

  // Calculate months from date range for API call
  const getMonthsFromDateRange = (): number => {
    if (!dateRange || !dateRange.from || !dateRange.to) {
      return 6; // Default to 6 months
    }

    const diffTime = Math.abs(dateRange.to.getTime() - dateRange.from.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Convert days to approximate months, with minimum of 1
    return Math.max(1, Math.ceil(diffDays / 30));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const months = getMonthsFromDateRange();
        const response = await artistAnalyticsService.getMonthlyFollowersData(artistId, months);
        setData(response.data);
        setArtistName(response.artistName);
      } catch (err) {
        setError('Failed to load monthly followers data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [artistId, dateRange, refreshKey]);

  // Function to sort months chronologically
  const sortMonthsChronologically = (months: string[]): string[] => {
    const monthOrder = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    return months.sort((a, b) => {
      // Extract month name (assuming format like "Jan 2024" or "Jan")
      const monthA = a.split(' ')[0];
      const monthB = b.split(' ')[0];
      
      // Extract year (if present)
      const yearA = a.split(' ')[1] ? parseInt(a.split(' ')[1]) : new Date().getFullYear();
      const yearB = b.split(' ')[1] ? parseInt(b.split(' ')[1]) : new Date().getFullYear();
      
      // Compare by year first, then by month
      if (yearA !== yearB) {
        return yearA - yearB;
      }
      
      return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
    });
  };

  const getChartOption = (): EChartsOption => {
    if (!data) return {};

    // Sort months chronologically for proper display
    const sortedMonths = sortMonthsChronologically(data.chart.xAxisData);
    const xLayout = getCategoryAxisLayout(sortedMonths.length);

    return {
      backgroundColor: '#ffffff',
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        textStyle: {
          color: '#1f2937'
        },
        confine: true,
        position: function (pt: number[]) {
          return [pt[0], '10%'];
        },
        formatter: function(params: any) {
          let content = `<div style="font-weight: bold; margin-bottom: 5px;">${params[0].name}</div>`;
          params.forEach((param: any) => {
            const value = param.value;
            const displayValue = value >= 1000000 
              ? `${(value / 1000000).toFixed(1)}M` 
              : value >= 1000 
                ? `${(value / 1000).toFixed(1)}K`
                : value.toString();
            content += `<div style="color: ${param.color};">● ${param.seriesName}: ${displayValue}</div>`;
          });
          return content;
        }
      },
      legend: {
        data: data.chart.series.map(s => s.name),
        textStyle: {
          color: '#6b7280'
        },
        bottom: '5%',
        left: 'center',
        itemGap: 20
      },
      grid: {
        left: '10%',
        right: '8%',
        bottom: '50%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: sortedMonths,
        axisLine: {
          lineStyle: {
            color: '#d1d5db'
          }
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: xLayout.fontSize,
          rotate: xLayout.rotate,
          interval: 0,
          margin: 15
        }
      },
      yAxis: {
        type: 'value',
        splitLine: {
          lineStyle: {
            color: '#e5e7eb',
            type: 'dashed'
          }
        },
        axisLine: {
          show: false
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: 12,
          formatter: function(value: number) {
            if (value >= 1000000) {
              return `${(value / 1000000).toFixed(1)}M`;
            } else if (value >= 1000) {
              return `${(value / 1000).toFixed(1)}K`;
            }
            return value.toString();
          }
        }
      },
      series: data.chart.series.map(item => {
        // Reorder series data to match sorted months
        const reorderedData = sortedMonths.map((month, index) => {
          const originalIndex = data.chart.xAxisData.indexOf(month);
          return originalIndex !== -1 ? item.data[originalIndex] : 0;
        });
        
        return {
          name: item.name,
          type: 'line' as const,
          data: reorderedData,
          smooth: item.smooth,
          lineStyle: item.lineStyle,
          itemStyle: item.itemStyle,
          symbol: item.symbol,
          symbolSize: item.symbolSize
        };
      })
    };
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="w-full h-full bg-transparent p-4 flex flex-col">
        <div className="flex items-center mb-6">
          <div>
            <div className="text-2xl font-bold text-gray-400">Loading...</div>
            <div className="text-sm text-gray-400">FETCHING DATA</div>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="w-full h-full bg-transparent p-4 flex flex-col">
        <div className="flex flex-1 items-center justify-center px-3 text-center">
          <p className="text-red-400 text-sm">{CHART_NO_DATA_FOR_ARTIST}</p>
        </div>
      </div>
    );
  }

  // totalValue now represents growth percentage, not sum
  const growthPercent = data.totalValue;
  const displayTotal = `${growthPercent >= 0 ? '+' : ''}${growthPercent.toFixed(1)}%`;

  return (
    <div className="w-full h-full bg-transparent p-4 flex flex-col" style={{ paddingBottom: '2rem' }}>
      <div className="flex items-center mb-6 flex-shrink-0">
        <div>
          <div className="text-2xl font-bold text-purple-400">{displayTotal}</div>
          <div className="text-sm text-gray-400">{getSummaryText()}</div>
        </div>
      </div>

      <div className="flex-1 w-full mb-4 min-h-0" style={{ minHeight: '350px', paddingBottom: '3rem' }}>
        <BaseChart
          option={getChartOption()}
          loading={loading}
          className="w-full h-full"
          style={{ paddingBottom: '1rem' }}
        />
      </div>

      {/* Data Table - Hidden */}
      {/* <div className="flex-1 w-full min-h-0 pb-4" style={{ maxHeight: '40%' }}>
        <div className="h-full border border-gray-700 rounded-lg overflow-hidden">
          <div className="h-full overflow-x-auto overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#1C1C1C] border-b border-gray-600 sticky top-0 z-10">
                <tr>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium w-[120px]">PLATFORM</th>
                  {sortMonthsChronologically(data.chart.xAxisData).map((month) => (
                    <th key={month} className="text-center py-3 px-2 text-gray-400 font-medium w-[80px]">
                      {month}
                    </th>
                  ))}
                  <th className="text-center py-3 px-4 text-gray-400 font-medium w-[80px]">OVERVIEW</th>
                </tr>
              </thead>
              <tbody>
                {data.table.map((row: MonthlyListenersTable, index) => (
                  <tr key={row.platform} className={`${index !== data.table.length - 1 ? 'border-b border-gray-700/50' : ''}`}>
                    <td className="py-3 px-4 w-[120px]">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: row.color }}
                        ></div>
                        <span className="text-gray-900 font-medium truncate">{row.platform}</span>
                      </div>
                    </td>
                    {sortMonthsChronologically(data.chart.xAxisData).map((month) => (
                      <td key={month} className="text-center py-3 px-2 text-gray-900 w-[80px]">
                        {formatNumber(row.monthlyData[month] || 0)}
                      </td>
                    ))}
                    <td className="text-center py-3 px-4 w-[80px]">
                      <span className={`font-medium ${row.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                        {row.overallGrowth}
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className="h-4">
                  <td colSpan={sortMonthsChronologically(data.chart.xAxisData).length + 2}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default MonthlyFollowersChart;
